// Offset-paginated contacts. /search accepts page (1-based) + page_size and
// returns total + total_pages, so the UI can offer "jump to last page". This
// is correct for INPACT (small dataset, low write rate); cursor-based
// pagination was overkill and forward-only.
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { searchContacts } from "../lib/api";
import { adaptContact } from "../lib/contactAdapter";
import type {
  Contact,
  FilterGroup,
  PageSize,
} from "../types/inpact";

interface UseContactsParams {
  filterGroup: FilterGroup;
  page: number;
  pageSize: PageSize;
  sortField?: string;
  sortDir?: "ASC" | "DESC";
  enabled?: boolean;
}

export interface UseContactsResult {
  rows: Contact[];
  total: number;
  totalPages: number;
  page: number;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  resultCode: "ok" | "no_matches" | null;
}

export function useContacts(params: UseContactsParams): UseContactsResult {
  const q = useQuery({
    queryKey: ["contacts", params],
    queryFn: () =>
      searchContacts({
        filterGroup: params.filterGroup,
        page: params.page,
        pageSize: params.pageSize,
        sortField: params.sortField,
        sortDir: params.sortDir,
      }),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
    enabled: params.enabled !== false,
  });

  return {
    rows: (q.data?.rows ?? []).map(adaptContact),
    total: q.data?.total ?? 0,
    totalPages: q.data?.total_pages ?? 0,
    page: q.data?.page ?? params.page,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    error: q.error ?? null,
    resultCode: q.data?.result_code ?? null,
  };
}
